import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { businessMapBoundsAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { api } from "@/lib/api-client";
import type { DetailedListingListItem } from "@/lib/types";

const EXPAND_FACTOR = 4;
const LIMIT = 6;

export function useNearbyListings(opts: {
  enabled: boolean;
  excludeIds?: number[];
}): {
  listings: DetailedListingListItem[];
  isNearby: boolean;
  isLoading: boolean;
} {
  const mapBounds = useAtomValue(businessMapBoundsAtom);

  const expandedBbox = useMemo(() => {
    if (!mapBounds) return undefined;
    const latSpan = mapBounds.ne.lat - mapBounds.sw.lat;
    const lngSpan = mapBounds.ne.lng - mapBounds.sw.lng;
    return {
      sw: {
        lat: Math.max(mapBounds.sw.lat - latSpan * EXPAND_FACTOR, -85),
        lng: Math.max(mapBounds.sw.lng - lngSpan * EXPAND_FACTOR, -180),
      },
      ne: {
        lat: Math.min(mapBounds.ne.lat + latSpan * EXPAND_FACTOR, 85),
        lng: Math.min(mapBounds.ne.lng + lngSpan * EXPAND_FACTOR, 180),
      },
    };
  }, [mapBounds]);

  const nearbyQuery = api.listings.get.useQuery(
    {
      bbox: expandedBbox,
      limit: LIMIT + (opts.excludeIds?.length ?? 0),
      orderByProperty: "relevance",
      orderByDirection: "desc",
    },
    {
      enabled: opts.enabled && !!expandedBbox,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const featuredQuery = api.listings.getFeatured.useQuery(
    { limit: LIMIT + (opts.excludeIds?.length ?? 0) },
    {
      enabled:
        opts.enabled &&
        nearbyQuery.isSuccess &&
        nearbyQuery.data.listings.length === 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const excludeSet = useMemo(
    () => new Set(opts.excludeIds ?? []),
    [opts.excludeIds],
  );

  const nearbyRaw = nearbyQuery.data?.listings ?? [];
  const featuredRaw = featuredQuery.data?.listings ?? [];

  const nearbyFiltered = useMemo(
    () =>
      nearbyRaw
        .filter((l) => !excludeSet.has(l.id))
        .slice(0, LIMIT),
    [nearbyRaw, excludeSet],
  );

  const featuredFiltered = useMemo(
    () =>
      featuredRaw
        .filter((l) => !excludeSet.has(l.id))
        .slice(0, LIMIT),
    [featuredRaw, excludeSet],
  );

  const isNearby = nearbyFiltered.length > 0;
  const listings = isNearby ? nearbyFiltered : featuredFiltered;

  const isLoading =
    nearbyQuery.isLoading ||
    (nearbyQuery.isSuccess &&
      nearbyRaw.length === 0 &&
      featuredQuery.isLoading);

  return { listings, isNearby, isLoading };
}
